import { FlaskConical, FolderGit2, RefreshCw, ScanSearch, ShieldCheck } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, cn, EmptyState, ErrorState, Segmented, SkeletonRows, Tabs, TabsContent, TabsList, TabsTrigger, toast, toUiError, useNow } from '@orgus/ui';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { DriftView } from '../features/discipline/DriftView.tsx';
import { fleetRatchet, fleetRows } from '../features/discipline/fleet.ts';
import { FleetTable } from '../features/discipline/FleetTable.tsx';
import { violationFeed, waiverStatus } from '../features/discipline/feed.ts';
import { OnboardDialog } from '../features/discipline/OnboardDialog.tsx';
import { PacksPanel } from '../features/discipline/PacksPanel.tsx';
import { RepoDetail } from '../features/discipline/RepoDetail.tsx';
import { RepoList } from '../features/discipline/RepoList.tsx';
import { RuleEditor } from '../features/discipline/RuleEditor.tsx';
import { SessionsView } from '../features/discipline/SessionsView.tsx';
import { SummaryStrip, SummaryStripSkeleton, disciplineVerdict } from '../features/discipline/SummaryStrip.tsx';
import { PageVerdict } from '../features/glance/PageVerdict.tsx';
import { useDiscipline } from '../features/discipline/useDiscipline.ts';
import { ViolationFeed } from '../features/discipline/ViolationFeed.tsx';
import { WaiversView } from '../features/discipline/WaiversView.tsx';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useOrgus, useProjects } from '../lib/store.ts';
import { DISCIPLINE_MIN_GAP_MS } from '@orgus/protocol';
import type { DisciplineReport, DisciplineState } from '@orgus/protocol';

const CHECK_WAIT_MS = 30_000;

/** Every `?tab=` the page answers. Waivers live under Rules (six tabs at most); `?tab=waivers` still opens them. */
export const DISCIPLINE_TABS = ['fleet', 'repos', 'violations', 'sessions', 'rules', 'waivers', 'drift'] as const;
export type DisciplineTab = (typeof DISCIPLINE_TABS)[number];
/** The tab a `?tab=` shows: waivers are a view of Rules. */
export function shownTab(tab: DisciplineTab): Exclude<DisciplineTab, 'waivers'> {
  return tab === 'waivers' ? 'rules' : tab;
}

/** Resolve with the project's next report (newer than `after`), or null after `timeoutMs`. */
function waitForReport(projectId: string, after: number, timeoutMs: number): Promise<DisciplineReport | null> {
  const find = () => ((useOrgus.getState().ext.discipline as DisciplineState | undefined)?.reports ?? []).find((r) => r.projectId === projectId && r.checkedAt > after) ?? null;
  return new Promise((resolve) => {
    const hit = find();
    if (hit) return resolve(hit);
    const timer = setTimeout(() => {
      unsub();
      resolve(null);
    }, timeoutMs);
    const unsub = useOrgus.subscribe(() => {
      const r = find();
      if (!r) return;
      clearTimeout(timer);
      unsub();
      resolve(r);
    });
  });
}

function DisciplineSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading discipline scores">
      <SummaryStripSkeleton cells={5} />
      <div className="rounded-card border border-border bg-surface">
        <SkeletonRows rows={6} />
      </div>
    </div>
  );
}

/**
 * Discipline: every watched repo held to its house rules. The fleet at a glance, each repo's
 * violations with their fixes, the feed across repos with send back, waive and open task,
 * scores per session and agent, the rule editor with packs and a dry run, waivers, and the
 * nightly drift scan.
 */
export default function Discipline() {
  const { rows, summary, checkNow, mode, state } = useDiscipline();
  const projects = useProjects();
  const sessions = useOrgus((s) => s.sessions);
  const now = useNow(60_000);
  const [params, setParams] = useSearchParams();
  const [checking, setChecking] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState<string | null | false>(false);
  const [checkError, setCheckError] = useState<unknown>(null);
  const tab = (DISCIPLINE_TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as DisciplineTab) : 'fleet';
  const selectedId = params.get('project') ?? rows[0]?.projectId ?? null;
  const selected = rows.find((r) => r.projectId === selectedId) ?? rows[0];
  const fleet = useMemo(() => fleetRows(rows, state, now), [rows, state, now]);
  const ratchet = useMemo(() => fleetRatchet(fleet), [fleet]);

  const setParam = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null) n.delete(k);
            else n.set(k, v);
          }
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );

  const run = useCallback(
    async (projectId?: string) => {
      const row = projectId ? rows.find((r) => r.projectId === projectId) : undefined;
      const before = row?.report?.checkedAt ?? 0;
      if (row && mode === 'live' && before && Date.now() - before < DISCIPLINE_MIN_GAP_MS) {
        toast('Already up to date', { description: `Checked ${Math.max(1, Math.round((Date.now() - before) / 1000))} s ago; try again in a minute.` });
        return;
      }
      setChecking(projectId ?? '*');
      setCheckError(null);
      try {
        await checkNow(projectId);
        if (mode === 'mock') {
          toast.success(projectId ? `Checked ${row?.name ?? 'the repo'}` : 'Checked every repo', 'Demo data: the scores were refreshed.');
        } else if (row) {
          const fresh = await waitForReport(row.projectId, before, CHECK_WAIT_MS);
          if (fresh) toast.success(`Checked ${row.name}`, fresh.error ? 'The check could not run; see the details.' : `Score ${fresh.score} of 100.`);
          else toast('Still checking', { description: `The score updates when ${row.name} reports.` });
        } else {
          toast.success('Checking every repo', 'Each machine checks its repos one at a time; scores update here as they report.');
        }
      } catch (e) {
        setCheckError(toUiError(e, { what: 'Could not start the check.', next: 'Make sure the machine is online, then try again.' }));
      } finally {
        setChecking(null);
      }
    },
    [checkNow, mode, rows],
  );

  const cmds = useMemo<Command[]>(
    () => [
      { id: 'discipline.check-all', title: 'Check every repo now', subtitle: 'Run the house rules on every watched repo', group: 'Actions', icon: ShieldCheck, order: 40, perform: () => void run() },
      { id: 'discipline.onboard', title: 'Onboard a repo', subtitle: 'Baseline report, suggested rules and a ratchet for existing debt', group: 'Actions', icon: ScanSearch, order: 41, perform: () => setOnboarding(selected?.projectId ?? null) },
      { id: 'discipline.rules', title: 'Edit house rules', subtitle: 'Rule editor with a dry run', group: 'Actions', icon: FlaskConical, order: 42, perform: () => setParam({ tab: 'rules' }) },
    ],
    [run, selected?.projectId, setParam],
  );
  useRegisterCommands(cmds, [cmds]);

  const openWaivers = (state?.waivers ?? []).filter((w) => {
    const s = waiverStatus(w, now);
    return s === 'active' || s === 'expiring';
  }).length;
  const openViolations = useMemo(() => violationFeed(rows).reduce((n, g) => n + g.errors + g.warns + g.hidden, 0), [rows]);
  const driftItems = (state?.drift ?? []).reduce((n, d) => n + Object.values(d.counts).reduce((a, b) => a + b, 0), 0);

  return (
    <PageBody wide>
      {rows.length > 0 && (
        <PageActions>
          <Button size="sm" variant="ghost" icon={<ScanSearch />} onClick={() => setOnboarding(selected?.projectId ?? null)} className="hidden sm:inline-flex">
            Onboard repo
          </Button>
          <Button size="sm" variant="primary" icon={<RefreshCw className={cn(checking === '*' && 'animate-spin')} />} onClick={() => void run()} disabled={checking !== null}>
            <span className="hidden sm:inline">Check every repo</span>
            <span className="sm:hidden">Check repos</span>
          </Button>
        </PageActions>
      )}
      <DataGate skeleton={<DisciplineSkeleton />}>
        {rows.length === 0 ? (
          <div className="rounded-card border border-border bg-surface">
            <EmptyState
              size="page"
              icon={<ShieldCheck />}
              title="No repos to check yet"
              description="Add a project and its agent checks it against the house rules."
              action={
                <Link to="/projects" className="inline-flex">
                  <Button variant="primary" icon={<FolderGit2 />}>
                    Go to Projects
                  </Button>
                </Link>
              }
            />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
          <PageVerdict
            v={disciplineVerdict(summary)}
            action={
              openViolations > 0 && tab !== 'violations' ? (
                <Button size="sm" variant="secondary" onClick={() => setParam({ tab: 'violations' })}>
                  Open violations
                </Button>
              ) : undefined
            }
          />
          <Tabs value={shownTab(tab)} onValueChange={(v) => setParam({ tab: v === 'fleet' ? null : v })} className="flex flex-col gap-6">
            {checkError != null && <ErrorState variant="inline" error={checkError} onRetry={() => void run()} />}
            {/* A phone cannot fit six tabs: the row scrolls inside itself, tighter, with a longer fade on the hidden edge so a cut tab reads as "more this way". */}
            <TabsList className="gap-4 sm:gap-6 max-sm:data-fade-start:mask-l-from-70% max-sm:data-fade-end:mask-r-from-70%" aria-label="Discipline views">
              <TabsTrigger value="fleet">
                Fleet
              </TabsTrigger>
              <TabsTrigger value="repos" count={rows.length}>
                Repos
              </TabsTrigger>
              <TabsTrigger value="violations" count={openViolations || undefined}>
                Violations
              </TabsTrigger>
              <TabsTrigger value="sessions" count={state?.sessions?.length || undefined}>
                Sessions
              </TabsTrigger>
              <TabsTrigger value="rules">
                Rules
              </TabsTrigger>
              <TabsTrigger value="drift" count={driftItems || undefined}>
                Drift
              </TabsTrigger>
            </TabsList>

            <TabsContent value="fleet" className="flex flex-col gap-6">
              <SummaryStrip summary={summary} ratchet={ratchet} onWaivers={() => setParam({ tab: 'waivers' })} onViolations={() => setParam({ tab: 'violations' })} />
              <FleetTable rows={fleet} onOpen={(id) => setParam({ tab: 'repos', project: id })} />
            </TabsContent>

            <TabsContent value="repos">
              <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                <RepoList rows={rows} selected={selected?.projectId ?? null} onSelect={(id) => setParam({ project: id })} />
                {selected && (
                  <RepoDetail
                    key={selected.projectId}
                    row={selected}
                    onCheck={() => void run(selected.projectId)}
                    checking={checking === selected.projectId || checking === '*'}
                    onOnboard={() => setOnboarding(selected.projectId)}
                    kept={!!state?.managed?.[selected.projectId]}
                  />
                )}
              </div>
            </TabsContent>

            <TabsContent value="violations">
              <ViolationFeed rows={rows} projectId={params.get('project')} onProject={(id) => setParam({ project: id })} />
            </TabsContent>

            <TabsContent value="sessions">
              <SessionsView state={state} projects={projects} sessions={sessions} />
            </TabsContent>

            <TabsContent value="rules" className="flex flex-col gap-6">
              <Segmented
                label="Rules or their waivers"
                value={tab === 'waivers' ? 'waivers' : 'rules'}
                onChange={(v) => setParam({ tab: v })}
                options={[
                  { value: 'rules', label: 'House rules' },
                  { value: 'waivers', label: 'Waivers', count: openWaivers || undefined },
                ]}
                className="self-start"
              />
              {tab === 'waivers' ? (
                <WaiversView waivers={state?.waivers ?? []} projects={projects} rows={rows} now={now} />
              ) : (
                <>
                  <RuleEditor rows={rows} state={state} projectId={params.get('project') ?? rows.find((r) => r.report && !r.report.error)?.projectId ?? null} onProject={(id) => setParam({ project: id })} />
                  <PacksPanel packs={state?.packs ?? []} pins={state?.pins ?? []} projects={projects} />
                </>
              )}
            </TabsContent>

            <TabsContent value="drift">
              <DriftView drift={state?.drift ?? []} schedule={state?.driftSchedule ?? { hour: 3, enabled: true }} pending={state?.pending?.drift ?? []} repos={rows.length} />
            </TabsContent>
          </Tabs>
          </div>
        )}
      </DataGate>
      {onboarding !== false && <OnboardDialog projects={projects} state={state} initial={onboarding} onClose={() => setOnboarding(false)} />}
    </PageBody>
  );
}
